import { defineStore } from "pinia";
import _ from "lodash";
import * as _debug from "debug";
import ApiClient from "@/lib/ApiClient";
import { sureClone } from "@/lib/DataUtils";
import { createStore } from "../storeHelper";

const debug = _debug(`${process.env.VUE_APP_PREFIX}:TemplateCollectionsStore`);

const DEFAULT_EDITOR = {
  id: null,
  name: null,
  description: null,
  organisationId: null,
};

export const useTemplateCollectionsStore = defineStore(
  "templateCollections",
  createStore({
    state: {
      templateCollections: {},
      editor: sureClone(DEFAULT_EDITOR),
    },

    getters: {
      templateCollectionsList: (state) => _.values(state.templateCollections),
      templateCollectionById: (state) => (id) => state.templateCollections[id],
      editorChanges() {
        return this.getEditorChanges("templateCollections");
      },
      editorHasChanges() {
        const diff = this.editorChanges;
        return diff && !_.isEmpty(diff);
      },
    },

    actions: {
      newTemplateCollectionToEditor() {
        this.set("editor", sureClone(DEFAULT_EDITOR));
      },

      addTemplateCollection(collection) {
        this.templateCollections[collection.id] = collection;
      },

      deleteVessel(collectionId) {
        // Note: keeping original naming bug from Vuex
        delete this.templateCollections[collectionId];
      },

      async load() {
        debug("load");
        const collections = await ApiClient.get(`/templateCollections`);
        debug("load", collections);
        this.set("templateCollections", _.keyBy(collections, "id"));
      },

      async loadTemplateCollectionToEditor(collectionId) {
        debug("loadTemplateCollectionToEditor", collectionId);
        const collection = this.templateCollections[collectionId];
        if (!collection) {
          throw new Error("Template collection not found");
        }
        this.set("editor", sureClone(collection));
      },

      async saveTemplateCollectionInEditor() {
        if (!this.editorHasChanges) {
          debug("saveTemplateCollectionInEditor", "No changes to save");
          return;
        }

        const collectionId = this.editor.id;
        const result = await ApiClient.put(
          `/templateCollections/${collectionId}`,
          this.editor,
        );

        if (result) {
          this.addTemplateCollection(result);

          // Cross-store sync
          const { useTemplatesStore } = await import("./templates");
          const templatesStore = useTemplatesStore();
          await templatesStore.syncChangesInTarget(result);
        }
        return result;
      },
    },
  }),
);
